;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:native-teams-frontend":true});}catch(e){}}();import { buildQueryParams } from '@workspace/ui/lib';
import type { NotificationCountCategory } from '@workspace/ui/lib';
import type { ApiResponse } from '@workspace/ui/types';
import { toast } from 'sonner';
import fetchAPI from '@/lib/fetch-api';
import type {
  BulkUpdateApiResponse,
  NotificationApiResponse,
  NotificationCategory,
  UpdateNotificationReadStatusRequest,
  UpdateNotificationReadStatusResponse,
  UpdateNotificationSettingRequest,
} from '@/types/notifications';

const NOTIFICATIONS_BASE_URL = process.env.NEXT_PUBLIC_EOR_API_URL
  ? process.env.NEXT_PUBLIC_EOR_API_URL.replace(/\/+$/, '')
  : undefined;

function buildNotificationsUrl(path: string): string {
  if (!NOTIFICATIONS_BASE_URL) {
    toast.error('Notifications are currently unavailable. Please contact support.');

    throw new Error('Notifications API base URL is not configured');
  }

  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  return `${NOTIFICATIONS_BASE_URL}${normalizedPath}`;
}

export async function getAllNotifications(type?: 'email' | 'in-app'): Promise<{
  data: NotificationApiResponse[];
  pagination: ApiResponse['pagination'] | null;
  message: string;
}> {
  const queryParams = buildQueryParams({ type });

  const response = await fetchAPI<NotificationApiResponse[]>(
    buildNotificationsUrl(`/notifications?${queryParams.toString()}`),
    {
      method: 'GET',
    }
  );

  if (!response.success) {
    console.error('Failed to fetch notifications:', response);
    throw new Error(response.error || response.message || 'Failed to fetch notifications');
  }

  const notifications = response.data as NotificationApiResponse[];

  return {
    data: notifications,
    pagination: response.pagination || null,
    message: response.message || 'Notifications fetched successfully',
  };
}

export async function updateNotificationReadStatus(
  request: UpdateNotificationReadStatusRequest
): Promise<ApiResponse<UpdateNotificationReadStatusResponse[]>> {
  const response = await fetchAPI<UpdateNotificationReadStatusResponse[]>(
    buildNotificationsUrl(`/notifications/${request.notification_id}`),
    {
      method: 'PUT',
      body: JSON.stringify({
        notification_user_id: request.notification_user_id,
        read: request.read,
      }),
    }
  );

  if (!response.success) {
    console.error('Failed to update notification read status:', response);
    throw new Error(
      response.error || response.message || 'Failed to update notification read status'
    );
  }

  return {
    success: response.success,
    data: response.data || [],
    message: response.message || 'Notification read status updated successfully',
  };
}

export async function markAllNotificationsAsRead(): Promise<
  ApiResponse<UpdateNotificationReadStatusResponse[]>
> {
  const response = await fetchAPI<BulkUpdateApiResponse>(
    buildNotificationsUrl('/notifications/bulk-update'),
    {
      method: 'PUT',
      body: JSON.stringify({
        read: true,
      }),
    }
  );

  if (!response.success) {
    console.error('Failed to mark all notifications as read:', response);
    throw new Error(
      response.error || response.message || 'Failed to mark all notifications as read'
    );
  }

  const notifications = (response.data as BulkUpdateApiResponse).data;

  return {
    success: response.success,
    data: notifications,
    message: response.message || 'All notifications marked as read successfully',
  };
}

export async function getNotificationCounts(): Promise<ApiResponse<NotificationCountCategory[]>> {
  const response = await fetchAPI<NotificationCountCategory[]>(
    buildNotificationsUrl('/notifications/counts'),
    {
      method: 'GET',
    }
  );

  if (!response.success) {
    throw new Error(response.error || response.message || 'Failed to fetch notification counts');
  }

  return {
    ...response,
    data: response.data || [],
    message: response.message || 'Notification counts fetched successfully',
  };
}

export async function getNotificationsByCategories(): Promise<ApiResponse<NotificationCategory[]>> {
  const response = await fetchAPI<NotificationCategory[]>(
    buildNotificationsUrl('/notifications/by-categories'),
    {
      method: 'GET',
    }
  );

  if (!response.success) {
    throw new Error(
      response.error || response.message || 'Failed to fetch notification categories'
    );
  }

  return {
    success: response.success,
    data: response.data || [],
    message: response.message || 'Notification categories fetched successfully',
  };
}

export async function updateNotificationSetting(
  request: UpdateNotificationSettingRequest
): Promise<{ message: string }> {
  const response = await fetchAPI(buildNotificationsUrl('/notifications/toggle-notifications'), {
    method: 'POST',
    body: JSON.stringify({
      notification_id: [request.notification_id],
      enabled: request.enabled,
    }),
  });

  if (!response.success) {
    throw new Error(response.error || response.message || 'Failed to update notification setting');
  }

  return {
    message: response.message || 'Notification setting updated successfully',
  };
}
